import { useEffect, useState } from "react";
import { Download, FileWarning, Loader2, ShieldCheck } from "lucide-react";

import { getExemptionDocument } from "@/data";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

/**
 * istisna / gider pusulasi akisinda influencer'in yukledigi istisna belgesini
 * gosterir. Belge PDF ya da gorsel olabilir; tipi blob'un content-type'indan
 * anlasiliyor. Dosya auth korumali proxy uzerinden cekiliyor.
 */
export default function ExemptionDocumentViewer({
  commissionDetailId,
  height = "800px",
}) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [contentType, setContentType] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let currentUrl = null;
    let cancelled = false;

    if (!commissionDetailId) return undefined;

    setLoading(true);
    setError(null);

    getExemptionDocument(commissionDetailId)
      .then((res) => {
        if (cancelled) return;
        currentUrl = URL.createObjectURL(res.data);
        setContentType(res.data?.type || "");
        setBlobUrl(currentUrl);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("İstisna belgesi yüklenemedi:", err);
        setError(
          err?.response?.status === 404
            ? "Bu kayıt için yüklenmiş bir istisna belgesi bulunamadı."
            : "Belge yüklenirken bir hata oluştu."
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
      if (currentUrl) {
        setTimeout(() => URL.revokeObjectURL(currentUrl), 1000);
      }
    };
  }, [commissionDetailId]);

  const isPdf = contentType.includes("pdf");

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-primary" />
            İstisna Belgesi
          </CardTitle>
          <CardDescription>
            Influencer'ın ödeme formuyla ilettiği belge
          </CardDescription>
        </div>
        {blobUrl && (
          <Button asChild variant="outline" size="sm">
            <a href={blobUrl} download={`istisna-belgesi-${commissionDetailId}`}>
              <Download className="mr-2 h-4 w-4" />
              İndir
            </a>
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {loading && (
          <div
            className="flex items-center justify-center rounded-md border bg-muted/30 text-muted-foreground"
            style={{ height }}
          >
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Belge yükleniyor...
          </div>
        )}

        {!loading && error && (
          <div
            className="flex flex-col items-center justify-center gap-2 rounded-md border bg-muted/30 p-6 text-center text-muted-foreground"
            style={{ height }}
          >
            <FileWarning className="h-6 w-6" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!loading && !error && blobUrl && (
          <div className="overflow-hidden rounded-md border">
            {isPdf ? (
              <object
                data={blobUrl}
                type="application/pdf"
                width="100%"
                height={height}
                aria-label="İstisna belgesi"
              >
                <div className="p-6 text-center text-sm text-muted-foreground">
                  Belge tarayıcıda görüntülenemedi.{" "}
                  <a className="underline" href={blobUrl} download>
                    İndirmek için tıklayın
                  </a>
                  .
                </div>
              </object>
            ) : (
              <img
                src={blobUrl}
                alt="İstisna belgesi"
                className="max-h-full w-full object-contain"
                style={{ maxHeight: height }}
              />
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
